import { useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderPriority, ProcessStep } from '../../common/enums/enums';
import { differenceDateTime } from '../../helpers/date';
import { isReconditioning } from '../../helpers/reconditioning';
import ActivityLogContentStructure from '../ActivityLogContentStructure';
import CollapsibleTable from '../CollapsibleTable';
import { GenDate, GenCurrency } from '../GenDataFormat';
import GeneralModal from '../Modal';
import StatusButton from '../StatusButton';
import ActionsColumn from './ActionsColumn';
import OrderTotalTime from './OrderTotalTime';
import { calculateLatestOrder } from './StatusChips/helper';
import { VehicleCardView } from './VehicleCardView';
import VehicleDetail from './VehicleDetail';
import WOCompletes from './WOCompletes';
import WorkOrderMenu from './WorkOrderMenu';

const DueDate = ({ order }) => {
  const { t } = useTranslation('global');
  if (isReconditioning(order)) {
    const latestOrderDate = calculateLatestOrder(order);
    const dueDate = new Date(latestOrderDate);
    dueDate.setMinutes(dueDate.getMinutes() + order?.package?.sla);
    if (
      order?.step === ProcessStep.WHOLE_SALE ||
      order?.step === ProcessStep.MECHANICAL
    )
      return '-';
    if (order?.step === ProcessStep.DONE) {
      const orderDate = calculateLatestOrder(order);
      const differenceDateInMinutes = differenceDateTime(
        orderDate,
        'minutes'
      ).minutes;
      const onTime = !!differenceDateInMinutes <= order?.package?.sla;
      return (
        <span style={{ color: onTime ? 'green' : 'red' }}>
          <GenDate date={dueDate} noTime={true} />{' '}
        </span>
      );
    }
    return <GenDate date={dueDate} noTime={true} /> || t('notAvailable');
  } else {
    if (order?.promiseDate) {
      const dueDate = new Date(order?.promiseDate);
      return <GenDate date={dueDate} noTime={true} /> || t('notAvailable');
    } else {
      const dueDate = new Date(order?.dateOrdered);
      const slaServicesTime = order?.relatedOrders?.map(item =>
        item?.services?.reduce((acc, val) => acc + val?.complianceTime, 0)
      );
      const totalSlaServicesTime = slaServicesTime?.reduce(
        (acc, item) => acc + item,
        0
      );
      dueDate.setMinutes(dueDate.getMinutes() + totalSlaServicesTime);
      return <GenDate date={dueDate} noTime={true} /> || t('notAvailable');
    }
  }
};

const isDesktopView = isMediumSizeBreakPoint =>
  !isMediumSizeBreakPoint ? '-' : '';

export default function WorkOrderForVehicle({
  data,
  loading,
  onClick,
  onLoadMore,
  history,
  isAddEstimateDisabled,
  onNewEstimateClick,
  handleStartDone,
  onAddTechnician,
  pintoTop,
  editRo,
  exportWO,
  enableOrderTagActions,
  enablePinToTop,
  enableTechnicianIcon,
  enableEditRo,
  enableExportWO,
  enableVehicleCardView = false,
  isVendor = false,
  enableStatusFilter = true,
  handleColumnSort = () => {},
  sortFields,
  sortColumnBy,
  isSortEnabled = false,
  moveToWholeSaleFn,
  moveToInProcessFn,
  enableStatusColumn = true,
  handleApi = () => {},
  photoCount,
  apiLoading = false,
}) {
  const { t } = useTranslation('workOrdersTable');
  const theme = useTheme();
  const isMediumSizeBreakPoint = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallSizeBreakPoint = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [activityLogOpen, setActivityLogOpen] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleActivityLog = order => {
    setActivityLogOpen(order);
  };

  const handleActivityLogClose = () => {
    setActivityLogOpen(null);
  };
  const woCompleteColumn = {
    field: 'woCompleted',
    headerName: !isSmallSizeBreakPoint ? t('vehicleTable.woCompleted') : '',
    valueGetter: order => <WOCompletes order={order} />,
  };

  const statusColumn = {
    field: 'status',
    headerName: !isMediumSizeBreakPoint ? t('vehicleTable.status') : '',
    valueGetter: order =>
      isReconditioning(order) ? (
        <StatusButton
          status={order?.step}
          order={order}
          sla={order?.package?.sla}
          isReconditioning={isReconditioning(order)}
        />
      ) : (
        isDesktopView(isMediumSizeBreakPoint)
      ),
  };
  const handleCloseMenu = () => setAnchorEl(null);

  const handleFieldSort = (fieldValue, sortBy) => {
    handleColumnSort(fieldValue, sortBy);
  };

  const actionsColumn = {
    field: 'actions',
    valueGetter: order => (
      <ActionsColumn
        order={order}
        onOpen={event => {
          setAnchorEl(event.currentTarget);
          setSelectedOrder(order);
        }}
        enableTechnicianIcon={enableTechnicianIcon}
      />
    ),
  };

  const columns = [
    {
      field: 'makeAndModel',
      headerName: t('vehicleTable.makeAndModel'),
      valueGetter: order =>
        `${order?.vehicle?.make} ${order?.vehicle?.model}` || '-',
    },
    {
      field: 'ro',
      headerName: t('vehicleTable.ro'),
      valueGetter: order => order?.ro ?? '-',
    },
    {
      field: 'dateOrdered',
      headerName: t('vehicleTable.dateOrdered'),
      valueGetter: order => <GenDate date={order.dateOrdered} noTime={true} />,
      isSortEnabled,
    },
    {
      field: 'stockNumber',
      headerName: t('vehicleTable.stockNumber'),
    },
    {
      field: 'vin',
      headerName: t('vehicleTable.vin'),
      valueGetter: order => order?.vehicle?.vin,
    },
    {
      field: 'amount',
      headerName: t('vehicleTable.amount'),
      valueGetter: order => <GenCurrency value={order.amount} />,
      isSortEnabled,
    },
    {
      field: 'totalTime',
      headerName: t('vehicleTable.totalTime'),
      valueGetter: order => <OrderTotalTime order={order} />,
      isSortEnabled,
    },
    {
      field: 'dueDate',
      headerName: t('vehicleTable.dueDate'),
      valueGetter: order => <DueDate order={order} />,
      isRecon: order => isReconditioning(order),
    },
  ];
  const hasNextPage =
    data?.pagination?.pageNumber < data?.pagination?.totalPages;

  if (isMediumSizeBreakPoint) {
    columns.unshift(woCompleteColumn);
    columns.unshift(actionsColumn);
    enableStatusColumn && columns.unshift(statusColumn);
  } else {
    enableStatusColumn && columns.push(statusColumn);
    columns.push(woCompleteColumn);
    columns.push(actionsColumn);
  }

  const dividerSection = (actual, next) => {
    return (
      enablePinToTop &&
      actual &&
      actual.priority === OrderPriority.HIGHEST &&
      next &&
      next.priority !== OrderPriority.HIGHEST
    );
  };

  return (
    <>
      <WorkOrderMenu
        anchorEl={anchorEl}
        activityLog={handleActivityLog}
        newEstimate={onNewEstimateClick}
        handleCloseMenu={handleCloseMenu}
        selectedOrder={selectedOrder}
        pintoTop={pintoTop}
        editRo={editRo}
        exportWO={exportWO}
        enablePinToTop={enablePinToTop}
        isAddEstimateDisabled={isAddEstimateDisabled}
        enableEditRo={enableEditRo}
        enableExportWO={enableExportWO}
        moveToWholeSaleFn={moveToWholeSaleFn}
        moveToInProcessFn={moveToInProcessFn}
      />
      <>
        <CollapsibleTable
          columns={columns}
          rows={data?.vehicleOrders || []}
          hasNextPage={hasNextPage}
          loading={loading}
          onClick={onClick}
          onLoadMore={onLoadMore}
          dividerSection={dividerSection}
          handleFieldSort={handleFieldSort}
          sortFields={sortFields}
          sortColumnBy={sortColumnBy}
          handleApi={handleApi}
          collpasibleGetter={vehicle => {
            return enableVehicleCardView ? (
              <VehicleCardView
                apiLoading={apiLoading}
                photoCount={photoCount}
                vehicle={vehicle}
                history={history}
                onNewEstimateClick={onNewEstimateClick}
                handleStartDone={handleStartDone}
                onAddTechnician={onAddTechnician}
                enableOrderTagActions={enableOrderTagActions}
                isVendor={isVendor}
                isAddEstimateDisabled={() => isAddEstimateDisabled(vehicle)}
                enableStatusFilter={enableStatusFilter}
              />
            ) : (
              <VehicleDetail
                vehicle={vehicle}
                history={history}
                isAddEstimateDisabled={() => isAddEstimateDisabled(vehicle)}
                onNewEstimateClick={onNewEstimateClick}
                handleStartDone={handleStartDone}
                onAddTechnician={onAddTechnician}
                enableOrderTagActions={enableOrderTagActions}
              />
            );
          }}
        />
      </>
      <GeneralModal
        modalState={!!activityLogOpen}
        forms={{
          activityLog: (
            <ActivityLogContentStructure
              isVehicle={true}
              data={activityLogOpen}
              closeModal={handleActivityLogClose}
            />
          ),
        }}
        initFormName="activityLog"
        handleModalState={setActivityLogOpen}
        closeModal={handleActivityLogClose}
      />
    </>
  );
}
